import React, { useContext, useMemo } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import Modal from "../Modal/Modal";
import ReleaseFlowContext from "../../context/releaseFlowContext";
import { RELEASE_FLOW_STEPS } from "../../context/releaseFlowTypes";
import ReleaseTextIcon from "../VectorIcons/ReleaseTextIcon";
import ReleaseFlowStepDestinations from "./ReleaseFlowStepDestinations";
import ReleaseFlowStepVersions from "./ReleaseFlowStepVersions";
import { getAttachmentSelectionKey } from "../../utils/getAttachmentSelectionKey";

const footerSx = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "16px",
  padding: "16px 24px",
  backgroundColor: "#e2e4ff",
};

const baseButtonSx = {
  fontFamily: "Globotipo Text",
  textTransform: "none",
  fontWeight: "bold",
  px: "24px",
  fontSize: "16px",
  height: "44px",
};

const outlinedButtonSx = {
  ...baseButtonSx,
  color: "#7F808E",
  borderColor: "#7F808E",
  "&:hover": {
    borderColor: "#7F808E",
    backgroundColor: "rgba(127,128,142,0.08)",
  },
};

const containedButtonSx = {
  ...baseButtonSx,
  color: "#fff",
  backgroundColor: "#1E9F69",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1a8a5b",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#fff",
    backgroundColor: "#A6DCC3",
  },
};

const modalBodySx = { backgroundColor: "#F0F1FF" };

const STEP_COMPONENTS = {
  [RELEASE_FLOW_STEPS.RELEASE_DESTINATION]: ReleaseFlowStepDestinations,
  [RELEASE_FLOW_STEPS.RELEASE_VERSION_MANAGEMENT]: ReleaseFlowStepVersions,
};

const ReleaseFlowFooterNextButton = ({ onNext, nextDisabled }) => (
  <Button
    variant="contained"
    onClick={onNext}
    disabled={nextDisabled}
    sx={containedButtonSx}
  >
    Seguinte
  </Button>
);

const ReleaseFlowFooterSaveButton = ({ onSave, isSubmitting }) => (
  <Button
    variant="contained"
    onClick={onSave}
    disabled={isSubmitting}
    sx={containedButtonSx}
  >
    {isSubmitting ? (
      <CircularProgress
        size={20}
        thickness={5}
        sx={{ color: "#fff", px: "24px" }}
      />
    ) : (
      "Salvar"
    )}
  </Button>
);

const FOOTER_PRIMARY_ACTION_COMPONENTS = {
  [RELEASE_FLOW_STEPS.RELEASE_DESTINATION]: ReleaseFlowFooterNextButton,
  [RELEASE_FLOW_STEPS.RELEASE_VERSION_MANAGEMENT]: ReleaseFlowFooterSaveButton,
};

const ReleaseFlowModalFooter = ({
  step,
  onBack,
  onNext,
  onSave,
  nextDisabled,
  isSubmitting,
}) => {
  const PrimaryActionButton = FOOTER_PRIMARY_ACTION_COMPONENTS[step];

  return (
    <Box sx={footerSx}>
      <Button
        variant="outlined"
        onClick={onBack}
        disabled={isSubmitting}
        sx={outlinedButtonSx}
      >
        Voltar
      </Button>

      <PrimaryActionButton
        onNext={onNext}
        onSave={onSave}
        nextDisabled={nextDisabled}
        isSubmitting={isSubmitting}
      />
    </Box>
  );
};

const ReleaseFlowModal = () => {
  const {
    isOpen,
    step,
    attachments,
    destinations,
    originalDestinations,
    isSubmitting,
    closeReleaseFlow,
    goToVersionManagement,
    goBackToDestination,
    submitRelease,
  } = useContext(ReleaseFlowContext);

  const isDestinationStep = step === RELEASE_FLOW_STEPS.RELEASE_DESTINATION;
  const StepComponent = STEP_COMPONENTS[step];

  const hasAnyDestinationChanged = useMemo(
    () =>
      attachments.some((attachment) => {
        const key = getAttachmentSelectionKey(attachment);
        const current = destinations[key];
        const original = originalDestinations[key];
        return ["channel", "production", "cultureHub"].some(
          (destination) => !!current?.[destination] !== !!original?.[destination]
        );
      }),
    [attachments, destinations, originalDestinations]
  );

  return (
    <Modal
      open={isOpen}
      onClose={closeReleaseFlow}
      icon={<ReleaseTextIcon color="#fff" />}
      title="Liberação de texto"
      bodySx={modalBodySx}
      maxWidth={1070}
      maxHeight="80vh"
      footer={
        <ReleaseFlowModalFooter
          step={step}
          onBack={isDestinationStep ? closeReleaseFlow : goBackToDestination}
          onNext={goToVersionManagement}
          onSave={submitRelease}
          nextDisabled={!hasAnyDestinationChanged}
          isSubmitting={isSubmitting}
        />
      }
    >
      <StepComponent />
    </Modal>
  );
};

export default ReleaseFlowModal;
